import React from 'react'
import Dotdotdot from 'react-dotdotdot'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import { Box, Grid, TableCell } from '@mui/material'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'
import { LanguageUtils, Text } from '@pbt/pbt-ui-components'
import { ChewySubmark } from '@pbt/pbt-ui-components/src/icons'

import type { RetailOrderLineItem } from '~/api/graphql/generated/types'
import {
  getIsRetailOrderLineItem,
  getItemCoveredByWplan,
  isAppointmentPenaltyProcedureItem,
  removeChewyPrefix,
} from '~/components/dashboard/invoices/invoiceUtils'
import { AutoshipInfo } from '~/components/dashboard/prescription/elements/AutoshipInfo/AutoshipInfo'
import MembershipIcon from '~/components/dashboard/soapV2/order/MembershipIcon'
import type { AutoshipFrequencyOption } from '~/constants/autoship'
import FeatureToggle from '~/constants/featureToggle'
import { FINANCE_TABLE_CELL_HEIGHT } from '~/constants/financeTable'
import {
  useIsChewyCheckoutEnabled,
  useIsNoShowCancellationPenaltyRefundEnabled,
} from '~/store/hooks/business'
import { getFeatureToggle } from '~/store/reducers/constants'
import type { InvoiceLineItem } from '~/types'
import { getPrescriptionType } from '~/utils/prescription'

const useStyles = makeStyles(
  (theme) => ({
    declined: {
      color: theme.colors.lowAccentText,
    },
    cellTextWrapper: {
      display: 'flex',
      alignItems: 'center',
    },
    groupedCell: {
      borderBottom: theme.constants.tabBorder,
    },
    chewyIcon: {
      height: '1.6rem',
      width: '1.6rem',
      marginRight: theme.spacing(0.625),
    },
    icon: {
      color: theme.colors.lowAccentText,
      '&:hover': {
        cursor: 'pointer',
      },
    },
    dragIcon: {
      color: theme.colors.lowAccentText,
      marginRight: theme.spacing(0.75),
    },
    membershipIcon: {
      width: 24,
      height: 24,
      marginRight: theme.spacing(0.5),
    },
    selectedRowText: {
      color: theme.colors.tabSelected,
    },
    title: {
      '&:hover': {
        cursor: 'pointer',
      },
    },
    inlineDotdotdot: {
      display: '-webkit-inline-box!important',
    },
    autoshipIcon: {
      width: '20px',
      height: '20px',
    },
    autoshipContainer: {
      display: 'inline-flex',
    },
  }),
  { name: 'RetailOrderItemNameCellBody' },
)

export interface RetailOrderItemNameCellBodyProps {
  handleSelectItem: (item: InvoiceLineItem | RetailOrderLineItem) => void
  isExpanded: boolean
  isGrouped: boolean
  isInvoice?: boolean
  isReadOnly: boolean
  item: InvoiceLineItem | RetailOrderLineItem
  nested?: boolean
  selectedItem: InvoiceLineItem | RetailOrderLineItem | undefined
  showAsDisabled?: Boolean
  tableCellClassName: string
  tableCellWithBorder?: string
  toggleExpand: (item: InvoiceLineItem | RetailOrderLineItem) => void
}

const RetailOrderItemNameCellBody = ({
  isInvoice = false,
  handleSelectItem,
  isGrouped,
  isExpanded,
  isReadOnly,
  showAsDisabled = false,
  item,
  nested,
  selectedItem,
  tableCellClassName,
  tableCellWithBorder,
  toggleExpand,
}: RetailOrderItemNameCellBodyProps) => {
  const classes = useStyles()

  const { t } = useTranslation(['Common', 'Invoices'])

  const isNoShowPenaltyRefundEnabled =
    useIsNoShowCancellationPenaltyRefundEnabled()
  const isChewyCheckoutEnabled = useIsChewyCheckoutEnabled()
  const isRetailOrderLineItem = getIsRetailOrderLineItem(item)

  const isAwoPmrRightRailEnabled = useSelector(
    getFeatureToggle(FeatureToggle.AWO_PMR_RIGHT_RAIL),
  )

  const isWplanItem = isRetailOrderLineItem ? false : Boolean(item.wplanLogId)
  const coveredByWplan = getItemCoveredByWplan(item)

  const hideDiscountMessageForPenaltyItem =
    isNoShowPenaltyRefundEnabled && isAppointmentPenaltyProcedureItem(item)
  const showMembershipDiscountAppliedMessage =
    coveredByWplan && !hideDiscountMessageForPenaltyItem

  const titleClasses = classNames(classes.title, {
    [classes.selectedRowText]: selectedItem && item.id === selectedItem.id,
    [classes.declined]: item.declined,
  })

  const statusLabel = item.declined
    ? `(${t('Common:DECLINED')})`
    : showMembershipDiscountAppliedMessage
      ? t('Invoices:TABLE.BODY.COVERED_BY_WELLNESS_PLAN')
      : ''

  const { isChewyActiveRx, isChewyReactiveRx } = isRetailOrderLineItem
    ? { isChewyActiveRx: true, isChewyReactiveRx: false }
    : getPrescriptionType(item.prescriptionType, item.origin)

  const displayChewySubmark = isRetailOrderLineItem
    ? isChewyCheckoutEnabled
    : (isChewyActiveRx || isChewyReactiveRx) && isChewyCheckoutEnabled

  const displayAutoshipInfo =
    displayChewySubmark &&
    getIsRetailOrderLineItem(item) &&
    Boolean(item.autoshipFrequency) &&
    Boolean(item.autoshipUnit?.name)

  let fieldName = `${LanguageUtils.getTranslatedFieldName(item)} ${statusLabel}`

  if (displayChewySubmark) {
    fieldName = removeChewyPrefix(fieldName)
  }

  return isGrouped && !isRetailOrderLineItem ? (
    <>
      <Grid
        container
        alignItems="center"
        className={classNames(tableCellClassName)}
        justifyContent="space-between"
        minHeight={FINANCE_TABLE_CELL_HEIGHT}
        py={1}
        wrap="nowrap"
      >
        <Grid
          container
          item
          alignItems="center"
          flexWrap="nowrap"
          onClick={() => (toggleExpand ? toggleExpand(item) : undefined)}
        >
          {!nested && !isReadOnly && isAwoPmrRightRailEnabled && (
            <DragIndicatorIcon className={classes.dragIcon} />
          )}
          {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          <Dotdotdot
            clamp={2}
            truncationChar={statusLabel ? `... ${statusLabel}` : '...'}
            useNativeClamp={false}
          >
            <Text
              strong
              className={titleClasses}
              variant={showAsDisabled ? 'lowAccent2' : 'body2'}
            >
              {`${item.groupName} ${statusLabel}`}
            </Text>
          </Dotdotdot>
        </Grid>
        {!isReadOnly && (
          <MoreHorizIcon
            className={classes.icon}
            id={`charge-widget-select-item-${item.id}_${item.logId}`}
            onClick={() => {
              handleSelectItem(item)
            }}
          />
        )}
      </Grid>
    </>
  ) : (
    <TableCell className={classNames(tableCellClassName, tableCellWithBorder)}>
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        minHeight={FINANCE_TABLE_CELL_HEIGHT}
        pl={nested ? 3 : 0}
        py={1}
        wrap="nowrap"
      >
        <Grid container alignItems="center" wrap="nowrap">
          {!nested && !isReadOnly && isAwoPmrRightRailEnabled && (
            <DragIndicatorIcon className={classes.dragIcon} />
          )}
          {isWplanItem && !item.declined && (
            <MembershipIcon
              className={classes.membershipIcon}
              limitReached={!coveredByWplan}
            />
          )}
          <Grid item>
            <div className={classes.cellTextWrapper}>
              {!isInvoice && displayChewySubmark && (
                <ChewySubmark className={classes.chewyIcon} />
              )}
              <Box columnGap={1} display="flex" flexWrap="wrap">
                <Dotdotdot clamp={2} className={classes.inlineDotdotdot}>
                  <Text
                    className={titleClasses}
                    title={fieldName}
                    variant={showAsDisabled ? 'lowAccent2' : 'body2'}
                  >
                    {fieldName}
                  </Text>
                </Dotdotdot>
                {displayAutoshipInfo && (
                  <AutoshipInfo
                    classes={{
                      icon: classes.autoshipIcon,
                      container: classes.autoshipContainer,
                    }}
                    frequency={
                      {
                        uom: item.autoshipUnit?.name,
                        value: item.autoshipFrequency,
                      } as AutoshipFrequencyOption
                    }
                  />
                )}
              </Box>
            </div>
          </Grid>
        </Grid>
        {!isReadOnly && (
          <MoreHorizIcon
            className={classes.icon}
            id={`charge-widget-select-item-${item.id}_${item.logId}`}
            onClick={() => {
              handleSelectItem(item)
            }}
          />
        )}
      </Grid>
    </TableCell>
  )
}

export default RetailOrderItemNameCellBody
